import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';

import DatepickerControlsForm from './DatepickerControlsForm';
import DatepickerHeader from './DatepickerHeader';
import DatepickerWeek from './DatepickerWeek';

const moment = extendMoment(Moment);

const styles = () => ({
  root: {
    position: 'absolute',
    width: 333,
    boxSizing: 'border-box',
    borderRadius: 4,
    zIndex: 1200,
    '&:focus': {
      outline: 0,
    },
  },
  rootPlacementBottomLeft: {
    top: '100%',
    left: 0,
  },
  rootPlacementTopLeft: {
    top: -346,
    left: 0,
  },
  rootPlacementMiddleLeft: {
    left: '100%',
    top: '50%',
    marginTop: -173,
  },
  rootPlacementBottomRight: {
    top: '100%',
    right: 0,
  },
  rootPlacementTopRight: {
    top: -346,
    right: 0,
  },
  rootPlacementMiddleRight: {
    right: '100%',
    top: '50%',
    marginTop: -173,
  },
  calendarTable: {
    padding: '0 24px 24px',
  },
});

class Datepicker extends Component {
  constructor(props) {
    super(props);
    let currentMonth = moment().startOf('month');
    if (this.date) {
      currentMonth = this.date.clone().startOf('month');
    }
    if (props.startDate) {
      currentMonth = props.activeDate === 'start'
        ? moment(props.startDate, props.dateFormat).startOf('day').startOf('month')
        : moment(props.endDate, props.dateFormat).startOf('day').startOf('month');
    }
    this.state = {
      currentMonth,
      rangeSelection: 'start',
      activeDate: null,
    };

    this.onDateSelect = this.onDateSelect.bind(this);
    this.onMonthChange = this.onMonthChange.bind(this);
    this.onDayMouseOver = this.onDayMouseOver.bind(this);
    this.onDayMouseOut = this.onDayMouseOut.bind(this);
    this.onRangeButtonClick = this.onRangeButtonClick.bind(this);

    this.timeoutDelay = 400;
  }

  onDateSelect(day) {
    const { range, activeDate } = this.props;
    const hours = this.date ? this.date.hours() : moment().hours();
    const minutes = this.date ? this.date.minutes() : moment().minutes();
    const newDate = day.clone()
      .hours(hours)
      .minutes(minutes);

    if (range) {
      if (this.state.rangeSelection === 'start') {
        if (activeDate === 'start') {
          this.props.onStartDateChange(newDate.format(this.props.dateFormat));
        } else {
          this.props.onEndDateChange(newDate.format(this.props.dateFormat));
        }
        this.setState({ rangeSelection: 'end' });
      } else if (activeDate === 'start') {
        this.props.onEndDateChange(newDate.format(this.props.dateFormat));
      } else {
        this.props.onStartDateChange(newDate.format(this.props.dateFormat));
      }
    } else {
      this.props.onDateChange(newDate.format(this.props.dateFormat));
    }
  }

  onMonthChange(newMonth) {
    this.setState({ currentMonth: newMonth });
  }

  onDayMouseOver(day) {
    this.setState({ activeDate: day });
  }

  onDayMouseOut() {
    this.setState({ activeDate: null });
  }

  onRangeButtonClick(range) {
    const { dateFormat } = this.props;
    const hours = this.date ? this.date.hours() : moment().hours();
    const minutes = this.date ? this.date.minutes() : moment().minutes();
    switch (range) {
      case 'today': {
        const newDate = moment()
          .hours(hours)
          .minutes(minutes);
        this.props.onStartDateChange(newDate.format(dateFormat));
        this.props.onEndDateChange(newDate.format(dateFormat));
        break;
      }
      case 'yesterday': {
        const newDate = moment().subtract(1, 'day')
          .hours(hours)
          .minutes(minutes);
        this.props.onStartDateChange(newDate.format(dateFormat));
        this.props.onEndDateChange(newDate.format(dateFormat));
        break;
      }
      case 'this week': {
        const newDateFrom = moment().startOf('week')
          .hours(hours)
          .minutes(minutes);
        const newDateTo = moment().endOf('week')
          .hours(hours)
          .minutes(minutes);
        this.props.onStartDateChange(newDateFrom.format(dateFormat));
        setTimeout(() => {
          this.props.onEndDateChange(newDateTo.format(dateFormat));
        }, this.timeoutDelay);
        break;
      }
      case 'last week': {
        const newDateFrom = moment().subtract(1, 'week').startOf('week')
          .hours(hours)
          .minutes(minutes);
        const newDateTo = moment().subtract(1, 'week').endOf('week')
          .hours(hours)
          .minutes(minutes);
        this.props.onStartDateChange(newDateFrom.format(dateFormat));
        setTimeout(() => {
          this.props.onEndDateChange(newDateTo.format(dateFormat));
        }, this.timeoutDelay);
        break;
      }
      case 'this month': {
        const newDateFrom = moment().startOf('month')
          .hours(hours)
          .minutes(minutes);
        const newDateTo = moment().endOf('month')
          .hours(hours)
          .minutes(minutes);
        this.props.onStartDateChange(newDateFrom.format(dateFormat));
        setTimeout(() => {
          this.props.onEndDateChange(newDateTo.format(dateFormat));
        }, this.timeoutDelay);
        break;
      }
      case 'last month': {
        const newDateFrom = moment().subtract(1, 'month').startOf('month')
          .hours(hours)
          .minutes(minutes);
        const newDateTo = moment().subtract(1, 'month').endOf('month')
          .hours(hours)
          .minutes(minutes);
        this.props.onStartDateChange(newDateFrom.format(dateFormat));
        setTimeout(() => {
          this.props.onEndDateChange(newDateTo.format(dateFormat));
        }, this.timeoutDelay);
        break;
      }
      default: {
        break;
      }
    }
  }

  get date() {
    const { date, maxDate, dateFormat } = this.props;
    if (date) {
      const dateMoment = moment(date, dateFormat).startOf('day');
      const maxDateMoment = moment(maxDate, dateFormat).startOf('day');
      if (dateMoment.isAfter(maxDateMoment)) {
        return null;
      }
      return dateMoment;
    }
    return null;
  }

  get startDate() {
    if (this.props.startDate) {
      return moment(this.props.startDate, this.props.dateFormat).startOf('day');
    }
    return null;
  }

  get endDate() {
    if (this.props.endDate) {
      return moment(this.props.endDate, this.props.dateFormat).startOf('day');
    }
    return null;
  }

  get minDate() {
    return moment(this.props.minDate, this.props.dateFormat);
  }

  get maxDate() {
    return moment(this.props.maxDate, this.props.dateFormat);
  }

  get weekArray() {
    const start = this.state.currentMonth.clone().startOf('month').startOf('week');
    const end = this.state.currentMonth.clone().endOf('month').endOf('week');

    const weeks = Array.from(moment.range(start, end).by('week'));

    const nestedWeeks = [];

    weeks.forEach((week) => {
      const endOfWeek = week.clone().endOf('week');
      nestedWeeks.push(Array.from(moment.range(week, endOfWeek).by('day')));
    });

    return nestedWeeks;
  }

  render() {
    const { currentMonth, activeDate } = this.state;
    const {
      classes,
      style,
      range,
      name,
      onBlur,
      disableTabFocus,
      placement,
      rangeButtons,
    } = this.props;
    const currentMonthNumber = this.state.currentMonth.get('month');
    return (
      <Paper
        className={classNames(classes.root, {
          [classes.rootPlacementBottomLeft]: placement === 'bottom-left',
          [classes.rootPlacementTopLeft]: placement === 'top-left',
          [classes.rootPlacementMiddleLeft]: placement === 'middle-left',
          [classes.rootPlacementBottomRight]: placement === 'bottom-right',
          [classes.rootPlacementTopRight]: placement === 'top-right',
          [classes.rootPlacementMiddleRight]: placement === 'middle-right',
        })}
        {... style ? { style } : {}}
        tabIndex="-1"
      >
        <DatepickerControlsForm
          form={`${name}-datepickerControls`}
          initialValues={{
            year: currentMonth.clone().startOf('year').format('YYYY'),
          }}
          currentMonth={currentMonth}
          minDate={this.minDate}
          maxDate={this.maxDate}
          onMonthChange={this.onMonthChange}
          disableTabFocus={disableTabFocus}
          onRangeButtonClick={this.onRangeButtonClick}
          rangeButtons={rangeButtons}
        />
        <div className={classes.calendarTable}>
          <Table>
            <DatepickerHeader />
            <TableBody>
              {this.weekArray.map(week => (
                <DatepickerWeek
                  key={week[0].toString()}
                  range={range}
                  date={this.date}
                  startDate={this.startDate}
                  endDate={this.endDate}
                  week={week}
                  currentMonthNumber={currentMonthNumber}
                  minDate={this.minDate}
                  maxDate={this.maxDate}
                  onDateSelect={this.onDateSelect}
                  onDayBlur={onBlur}
                  {...range ? {
                    onDayMouseOver: this.onDayMouseOver,
                    onDayMouseOut: this.onDayMouseOut,
                    activeDate,
                  } : {}}
                  disableTabFocus={disableTabFocus}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </Paper>
    );
  }
}

Datepicker.propTypes = {
  name: PropTypes.string.isRequired,
  date: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(Moment),
  ]),
  startDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(Moment),
  ]),
  endDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(Moment),
  ]),
  minDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(Moment),
  ]).isRequired,
  maxDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(Moment),
  ]).isRequired,
  dateFormat: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootPlacementBottomLeft: PropTypes.string.isRequired,
    rootPlacementTopLeft: PropTypes.string.isRequired,
    rootPlacementMiddleLeft: PropTypes.string.isRequired,
    rootPlacementBottomRight: PropTypes.string.isRequired,
    rootPlacementTopRight: PropTypes.string.isRequired,
    rootPlacementMiddleRight: PropTypes.string.isRequired,
    calendarTable: PropTypes.string.isRequired,
  }).isRequired,
  style: PropTypes.shape({
    transform: PropTypes.string,
  }),
  activeDate: PropTypes.oneOf(['start', 'end']),
  placement: PropTypes.oneOf([
    'bottom-left',
    'top-left',
    'bottom-right',
    'top-right',
    'middle-left',
    'middle-right',
  ]),
  onDateChange: PropTypes.func,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  range: PropTypes.bool,
  onBlur: PropTypes.func,
  disableTabFocus: PropTypes.bool,
  rangeButtons: PropTypes.bool,
};

Datepicker.defaultProps = {
  date: null,
  startDate: null,
  endDate: null,
  style: null,
  range: false,
  disableTabFocus: false,
  activeDate: 'start',
  placement: 'bottom-left',
  onDateChange: () => {},
  onStartDateChange: () => {},
  onEndDateChange: () => {},
  onBlur: () => {},
  rangeButtons: false,
};

export default withStyles(styles)(Datepicker);



// WEBPACK FOOTER //
// ./src/components/fields/DatepickerField/Datepicker.js